import * as React from "react";
import { useDialog } from "@shared-ui/dialog-context";
import { Viewport, ViewSmall, ViewLarge } from "@shared-ui/viewport-context";
import { UitkLayoutFlex } from "@egds/react-core/layout-flex";

import DestinationSampleReviews from "./components/DestinationSampleReviews";
import { DestinationOverallRating } from "./components/DestinationOverallRating";
import DestinationUserReviewsDialog from "./components/DestinationUserReviewsDialog";
import { DestinationUserReviewsFlexModuleResult } from "typings/microserviceModels/destination-user-reviews-flex-module";
import { DestinationUserReviewsProps, JustifyContentValues } from "./typings";
import { ViewType } from "./DestinationUserReviewsEnums";
import { withStores } from "stores";
import { observer } from "mobx-react";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkSheetTransition } from "@egds/react-core/sheet";

const NAME_FOR_TRACKING = "TG.LP.Dest.UserReviews";

const DestinationUserReviews = withStores("flexModuleModelStore")(
  observer((props: DestinationUserReviewsProps) => {
    const { templateComponent, flexModuleModelStore } = props;
    const {
      metadata: { id },
      config,
    } = templateComponent;
    const model = flexModuleModelStore.getModel(id) as DestinationUserReviewsFlexModuleResult | null;
    const [isDialogOpen, dialogActions, DestinationDialogComponent] = useDialog("destination-reviews");

    if (!model) {
      return null;
    }

    const { destinationName, reviews, aggregateReview, countPerRating, title, hideDate } = model;
    const { formatText } = useLocalization();

    if (!(destinationName && reviews?.length && aggregateReview && countPerRating)) {
      return null;
    }

    const { totalReviews, totalRatings, averageRating } = aggregateReview;

    if (!(totalReviews && totalRatings && averageRating)) {
      return null;
    }

    const getLayoutWithFlexAlignment = (justifyContent: JustifyContentValues = "end") => (
      <UitkLayoutFlex
        className="DestinationUserReviews"
        id={id}
        data-fm={config.fmId}
        justifyContent={justifyContent}
        alignItems="center"
      >
        <DestinationOverallRating
          totalReviews={totalReviews}
          avgRating={averageRating}
          toggleAllDestinationReviews={dialogActions.toggleDialog}
          nameForTracking={NAME_FOR_TRACKING}
        />
      </UitkLayoutFlex>
    );

    return (
      <>
        {model.view === ViewType.DETAIL && (
          <div className="DestinationUserReviews detailView" id={id} data-fm={config.fmId}>
            <DestinationSampleReviews
              title={title}
              nameForTracking={NAME_FOR_TRACKING}
              reviews={reviews}
              destinationName={destinationName}
              totalReviews={totalReviews}
              totalRatings={totalRatings}
              countPerRating={countPerRating}
              avgRating={averageRating}
              toggleAllDestinationReviews={dialogActions.toggleDialog}
              hideDate={hideDate}
            />
          </div>
        )}
        {model.view === ViewType.SUMMARY && (
          <Viewport>
            <ViewSmall>{getLayoutWithFlexAlignment("space-between")}</ViewSmall>
            <ViewLarge>{getLayoutWithFlexAlignment()}</ViewLarge>
          </Viewport>
        )}
        <UitkSheetTransition isVisible={isDialogOpen}>
          <DestinationDialogComponent>
            <DestinationUserReviewsDialog
              nameForTracking={NAME_FOR_TRACKING}
              reviewDialogTitle={formatText("destinationReviews.cityReviews", destinationName)}
              reviews={reviews}
              toggleAllDestinationReviews={dialogActions.toggleDialog}
              isOpen={isDialogOpen}
            />
          </DestinationDialogComponent>
        </UitkSheetTransition>
      </>
    );
  })
);

export default DestinationUserReviews;
